import React, { useCallback } from 'react';
import { Accept, DropzoneInputProps, useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import { RoutalPalette } from '@nx-smartmonkey/ui';
import Button from '../../../../react-components/Button';

const StyledDropzoneContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  outline: none;
  border-radius: inherit;
`;

interface StyledDropzoneInputProps extends DropzoneInputProps {
  isDragAccept: boolean;
  isDragReject: boolean;
}

const StyledDropzoneContent = styled.div`
  width: 100%;
  height: 100%;
  outline: none;
  border-radius: inherit;
  background-color: transparent;
  border: 2px dashed transparent;
  transition: border 0.25s;
  :hover {
    border-color: ${RoutalPalette.primary40};
  }
  ${(props: StyledDropzoneInputProps) => {
    if (props.isDragAccept) {
      return `
        border-color: ${RoutalPalette.primary40};
        background-color: #cfefcf;
      `;
    }
    if (props.isDragReject) {
      return `background-color: #ffcfcf`;
    }
    return ``;
  }}
`;

interface ImportVehiclesDialogUploadProps {
  accept?: Accept;
  uploadButtonProps?: any;
  dropzoneStyles?: React.CSSProperties;
  onFileUpload: (file: File) => void;
}

/**
 * Component used as upload zone
 * @param props Upload accept type, dropzone styles and callback of upload function.
 */
const ImportVehiclesDialogUpload = ({
  children,
  uploadButtonProps,
  accept,
  dropzoneStyles,
  onFileUpload,
}: React.PropsWithChildren<ImportVehiclesDialogUploadProps>) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.length > 0 && onFileUpload(acceptedFiles[0]);
    },
    [onFileUpload]
  );

  const { getRootProps, getInputProps, open, isDragAccept, isDragReject } = useDropzone({
    multiple: false,
    accept: accept ?? {
      // 'text/*': [`.csv`],
      'application/vnd.ms-excel': [`.xls`],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [`.xlsx`],
    },
    onDrop,
  });

  return (
    <StyledDropzoneContainer {...getRootProps()}>
      {/* This input is required by the library */}
      <input {...getInputProps()} />
      <StyledDropzoneContent isDragReject={isDragReject} isDragAccept={isDragAccept} style={dropzoneStyles ?? {}}>
        {children}
        {uploadButtonProps ? (
          <Button
            {...uploadButtonProps}
            onClick={open}
            style={{
              marginTop: `8px`,
              ...(uploadButtonProps.style ?? {}),
            }}
          />
        ) : null}
      </StyledDropzoneContent>
    </StyledDropzoneContainer>
  );
};

export default ImportVehiclesDialogUpload;
